import React from 'react';
import { Anime, TierGrade, TIER_CONFIGS } from '../types/anime';
import { AnimeCard } from './AnimeCard';
import { formatImageUrl } from '../utils/helpers';

interface TierBoardProps {
  animeList: Anime[];
  isSortMode: boolean;
  onQuickPlus: (id: string) => void;
  onQuickMinus: (id: string) => void;
  onToggleNotify: (id: string) => void;
  onEdit: (anime: Anime) => void;
  onOpenDetail: (anime: Anime) => void;
  onReorder: (draggedId: string, targetId: string) => void;
  onMoveUp?: (id: string) => void;
  onMoveDown?: (id: string) => void;
  onMoveToTier?: (animeId: string, targetGrade: TierGrade) => void;
  onStartSortMode?: () => void;
}

export const TierBoard: React.FC<TierBoardProps> = ({
  animeList,
  isSortMode,
  onQuickPlus,
  onQuickMinus,
  onToggleNotify,
  onEdit,
  onOpenDetail,
  onReorder,
  onMoveUp,
  onMoveDown,
  onMoveToTier,
  onStartSortMode,
}) => {
  const [draggedAnimeId, setDraggedAnimeId] = React.useState<string | null>(null);
  const [hoverTargetId, setHoverTargetId] = React.useState<string | null>(null);
  const [hoverTierGrade, setHoverTierGrade] = React.useState<TierGrade | null>(null);
  const [touchPosition, setTouchPosition] = React.useState<{ x: number; y: number } | null>(
    null
  );

  const draggedAnimeIdRef = React.useRef<string | null>(null);
  const hoverTargetIdRef = React.useRef<string | null>(null);
  const hoverTierGradeRef = React.useRef<TierGrade | null>(null);

  draggedAnimeIdRef.current = draggedAnimeId;
  hoverTargetIdRef.current = hoverTargetId;
  hoverTierGradeRef.current = hoverTierGrade;

  const tiers: TierGrade[] = ['S', 'A+', 'A', 'B', 'C', 'EX'];

  const handleDragStart = (e: React.DragEvent, id: string) => {
    if (!isSortMode) return;
    setDraggedAnimeId(id);
    draggedAnimeIdRef.current = id;
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, targetId?: string) => {
    if (!isSortMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (targetId && targetId !== draggedAnimeIdRef.current) {
      setHoverTargetId(targetId);
      setHoverTierGrade(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    if (!isSortMode) return;
    e.preventDefault();
    const sourceId = draggedAnimeIdRef.current || e.dataTransfer.getData('text/plain');
    if (sourceId && sourceId !== targetId) {
      onReorder(sourceId, targetId);
    }
    setDraggedAnimeId(null);
    setHoverTargetId(null);
    setHoverTierGrade(null);
  };

  const handleTierDragOver = (e: React.DragEvent, grade: TierGrade) => {
    if (!isSortMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHoverTierGrade(grade);
  };

  const handleTierDrop = (e: React.DragEvent, grade: TierGrade) => {
    if (!isSortMode) return;
    e.preventDefault();
    const sourceId = draggedAnimeIdRef.current || e.dataTransfer.getData('text/plain');
    if (sourceId && onMoveToTier) {
      onMoveToTier(sourceId, grade);
    }
    setDraggedAnimeId(null);
    setHoverTargetId(null);
    setHoverTierGrade(null);
  };

  const handleDragEnd = () => {
    setDraggedAnimeId(null);
    setHoverTargetId(null);
    setHoverTierGrade(null);
  };

  const handleTouchStartDrag = (e: React.TouchEvent, anime: Anime) => {
    const touch = e.touches[0];
    if (!touch) return;

    setDraggedAnimeId(anime.id);
    draggedAnimeIdRef.current = anime.id;
    setTouchPosition({ x: touch.clientX, y: touch.clientY });

    const handleWindowTouchMove = (ev: TouchEvent) => {
      const currentTouch = ev.touches[0];
      if (!currentTouch) return;
      if (ev.cancelable) ev.preventDefault();

      setTouchPosition({ x: currentTouch.clientX, y: currentTouch.clientY });

      const elem = document.elementFromPoint(currentTouch.clientX, currentTouch.clientY);
      const targetCard = elem?.closest('[data-anime-id]');
      const targetTier = elem?.closest('[data-tier-grade]');

      if (targetCard) {
        const tId = targetCard.getAttribute('data-anime-id');
        if (tId && tId !== draggedAnimeIdRef.current) {
          setHoverTargetId(tId);
          hoverTargetIdRef.current = tId;
          setHoverTierGrade(null);
          hoverTierGradeRef.current = null;
          return;
        }
      }

      if (targetTier) {
        const tGrade = targetTier.getAttribute('data-tier-grade') as TierGrade;
        if (tGrade) {
          setHoverTargetId(null);
          hoverTargetIdRef.current = null;
          setHoverTierGrade(tGrade);
          hoverTierGradeRef.current = tGrade;
          return;
        }
      }

      setHoverTargetId(null);
      hoverTargetIdRef.current = null;
      setHoverTierGrade(null);
      hoverTierGradeRef.current = null;
    };

    const handleWindowTouchEnd = () => {
      const activeId = draggedAnimeIdRef.current;
      const targetId = hoverTargetIdRef.current;
      const targetGrade = hoverTierGradeRef.current;

      if (activeId && targetId && targetId !== activeId) {
        onReorder(activeId, targetId);
      } else if (activeId && targetGrade && onMoveToTier) {
        onMoveToTier(activeId, targetGrade);
      }

      setDraggedAnimeId(null);
      draggedAnimeIdRef.current = null;
      setHoverTargetId(null);
      hoverTargetIdRef.current = null;
      setHoverTierGrade(null);
      hoverTierGradeRef.current = null;
      setTouchPosition(null);

      window.removeEventListener('touchmove', handleWindowTouchMove);
      window.removeEventListener('touchend', handleWindowTouchEnd);
      window.removeEventListener('touchcancel', handleWindowTouchEnd);
    };

    window.addEventListener('touchmove', handleWindowTouchMove, { passive: false });
    window.addEventListener('touchend', handleWindowTouchEnd);
    window.addEventListener('touchcancel', handleWindowTouchEnd);
  };

  const draggedAnime = draggedAnimeId
    ? animeList.find((a) => a.id === draggedAnimeId)
    : null;

  return (
    <main className="max-w-7xl mx-auto px-2.5 sm:px-6 py-3 sm:py-5 space-y-3 sm:space-y-5 relative pb-8">
      {/* Floating Drag Preview on Mobile */}
      {touchPosition && draggedAnime && (
        <div
          style={{
            left: `${touchPosition.x}px`,
            top: `${touchPosition.y}px`,
            transform: 'translate(-50%, -65%) scale(1.08)',
          }}
          className="fixed z-50 pointer-events-none w-28 sm:w-36 bg-slate-900 border-2 border-amber-400 rounded-xl overflow-hidden shadow-2xl shadow-black ring-4 ring-amber-400/40 select-none"
        >
          <div className="relative aspect-[3/4] w-full bg-slate-950 overflow-hidden">
            <img
              src={formatImageUrl(draggedAnime.imageUrl)}
              alt={draggedAnime.title}
              className="w-full h-full object-cover pointer-events-none"
            />
            <div className="absolute top-1 left-1 bg-amber-500 text-slate-950 px-1.5 py-0.5 rounded text-[9px] font-bold shadow">
              กำลังจัดเรียง
            </div>
          </div>
          <div className="p-1.5 bg-slate-900 text-[10px] font-medium text-white truncate text-center">
            {draggedAnime.title}
          </div>
        </div>
      )}

      {tiers.map((tierGrade) => {
        const config = TIER_CONFIGS[tierGrade];
        const tierAnime = animeList
          .filter((a) => a.grade === tierGrade)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const isTierHovered = hoverTierGrade === tierGrade;

        return (
          <section
            key={tierGrade}
            data-tier-grade={tierGrade}
            onDragOver={(e) => handleTierDragOver(e, tierGrade)}
            onDrop={(e) => {
              if (tierAnime.length === 0) {
                handleTierDrop(e, tierGrade);
              }
            }}
            className={`cv-auto relative rounded-3xl overflow-hidden transition-colors duration-150 ${
              isTierHovered
                ? 'bg-amber-500/[0.06] border border-amber-400/70 ring-2 ring-amber-400/40'
                : 'glass-panel'
            }`}
          >
            {/* Accent line in the tier's colors */}
            <div className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${config.gradient} opacity-70`} />

            {/* Tier Header */}
            <div className="px-3 sm:px-5 pt-3 sm:pt-4 flex items-center gap-3">
              <span
                className={`min-w-[2.5rem] h-10 sm:min-w-[2.75rem] sm:h-11 px-2 rounded-2xl bg-gradient-to-br ${config.gradient} grid place-items-center text-white font-extrabold text-lg sm:text-xl tracking-tight shadow-lg shadow-black/30 ring-1 ring-white/20`}
                style={{ fontFamily: '"Plus Jakarta Sans", Kanit, sans-serif' }}
              >
                {tierGrade}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm sm:text-base font-semibold text-white leading-tight">
                  {config.subtitle.split(' / ')[0]}
                </div>
                <div className="text-[11px] sm:text-xs text-slate-500 truncate">
                  {config.subtitle.split(' / ').slice(1).join(' · ') || config.title}
                </div>
              </div>
              <span className="text-xs text-slate-400 font-medium tabular-nums shrink-0">
                {tierAnime.length} เรื่อง
              </span>
            </div>

            {/* Tier Cards Grid */}
            <div className="p-2.5 sm:p-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3.5 items-stretch">
              {tierAnime.length === 0 ? (
                <div className="col-span-full py-4 rounded-2xl border border-dashed border-white/[0.07] text-center text-slate-600 text-xs">
                  {isSortMode ? 'ลากการ์ดมาวางที่นี่' : 'ยังไม่มีเรื่องใน Tier นี้'}
                </div>
              ) : (
                tierAnime.map((anime, idx) => (
                  <AnimeCard
                    key={anime.id}
                    anime={anime}
                    index={idx}
                    isSortMode={isSortMode}
                    isDragging={draggedAnimeId === anime.id}
                    isDropTarget={hoverTargetId === anime.id}
                    onQuickPlus={onQuickPlus}
                    onQuickMinus={onQuickMinus}
                    onToggleNotify={onToggleNotify}
                    onEdit={onEdit}
                    onOpenDetail={onOpenDetail}
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    onDragStart={handleDragStart}
                    onDragOver={(e) => handleDragOver(e, anime.id)}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    onTouchStartDrag={handleTouchStartDrag}
                    onStartSortMode={onStartSortMode}
                  />
                ))
              )}
            </div>
          </section>
        );
      })}
    </main>
  );
};
